/**
 * @file getValues
 *
 * @fileoverview Helpers to get initial values based on field definition or
 * entity values if passed.
 */

import { EntityField, EntityDefinition } from '~/jobiqo/models/entity';

/**
 * Get values out of the entity that was passed.
 *
 * When we receive an entity the form items should be filled from the
 * values in the entity itself. This function will return the appropriate
 * values to the form.
 *
 * @return {{}}
 *   The values object that react-hook-form expects.
 */
export const getEntityValues = (
  entity,
  definition: EntityDefinition,
  overrides: any,
): {} => {
  // The reducer will go through each field in the entity that was passed and assign a new
  // item in the result object with the value that the entity has for each field.
  return definition.fields.reduce((entityValues, field: EntityField) => {
    if (entity && entity[field.id]) {
      switch (field.type) {
        case 'entity_reference':
          entityValues[field.id] =
            field.maxNumItems === 1
              ? // There are some special fields that have maxNumItems=1,
                // but they are still treated as array on the API level
                // See: https://jobiqo.eu.teamwork.com/spaces/jobiqo-decoupled-development/page/7324-job-special-fields
                // See: https://jobiqo.eu.teamwork.com/spaces/jobiqo-decoupled-development/page/7326-resume-special-fields
                entity[field.id] instanceof Array && entity[field.id].length > 0
                ? (entityValues[field.id] = entity[field.id][0])
                : (entityValues[field.id] = entity[field.id])
              : (entityValues[field.id] = entity[field.id].map((i) => i));
          break;
        case 'datetime':
          entityValues[field.id] = entity[field.id];
          break;
        case 'daterange':
          if (field.maxNumItems === 1 || !Array.isArray(entity[field.id])) {
            entityValues[field.id] = createDateRange(entity[field.id]);
          } else {
            // Handling multiple dateranges
            entityValues[field.id] = entity[field.id].map(createDateRange);
          }
          break;
        default:
          entityValues[field.id] = entity[field.id];
          break;
      }
    }

    // If customized use direct value.
    if (overrides[field.id]) {
      entityValues[field.id] = entity[field.id];
    }

    return entityValues;
  }, {});
};

/**
 * Get the default values from the entity definition.
 *
 * When we have no entity being passed means we are in a add / create form, we should still use the
 * values defined in the backend as default values.
 *
 * @return {{}}
 *   The values object that react-hook-form expects.
 */
export const getDefaultValues = (definition: EntityDefinition): {} => {
  // The reducer will go through each field in the entity definition and assign a new
  // item in the result object with that exact value.
  return definition.fields.reduce((defaultValues, field: EntityField) => {
    // check for default values.
    defaultValues[field.id] =
      field.type === 'boolean' && field.defaultValue === ''
        ? false // make sure if we have a boolean field with '' as default value we cast to false.
        : (!field.defaultValue && field.maxNumItems) === -1
        ? [] // No default value multiple field (default to empty array).
        : field.defaultValue
        ? field.type === 'integer'
          ? Number.parseInt(field.defaultValue) // Parse integer default values.
          : field.defaultValue // Has default value use that.
        : field.isReference || field.id === 'id' || field.type === 'integer'
        ? null // Reference fields, ids and integer we set to null by default.
        : // No default value (or empty string) and boolean set to false
          ''; // String fields.

    return defaultValues;
  }, {});
};

/**
 * Get the initial values for this form.
 *
 * Should respect defaultValue when set and should use the entity passed if appropriate.
 * Will go for each item and set the appropriate default value or value (if in edit mode)
 * and undefined otherwise.
 *
 * @return {{}}
 *   The values object that react-hook-form expects.
 */
export const getInitialValues = (
  entity,
  definition: EntityDefinition,
  overrides: any,
  extraInitialValues: any,
): {} => {
  const initialValues = entity
    ? getEntityValues(entity, definition, overrides)
    : getDefaultValues(definition);

  return { ...initialValues, ...extraInitialValues };
};

/**
 * Create a date range object.
 *
 * @param dateRange
 *   The date range object.
 *
 * @return {{}}
 *   The date range object.
 */
export const createDateRange = (dateRange): {} => ({
  startDate: dateRange?.startDate,
  endDate: dateRange?.endDate,
  ...(dateRange?.untilNow ? { untilNow: dateRange.untilNow } : {}),
});
